import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  XMarkIcon,
  PlusIcon,
  TrashIcon
} from '@heroicons/react/24/outline'
import { CreateMemorialData } from '../services/memorialApi'
import PhotoUpload from './PhotoUpload'

interface CreateMemorialModalProps {
  isOpen: boolean
  onClose: () => void
  onCreateMemorial: (memorialData: CreateMemorialData) => Promise<void>
  isLoading?: boolean
}

export default function CreateMemorialModal({
  isOpen,
  onClose,
  onCreateMemorial,
  isLoading = false
}: CreateMemorialModalProps) {
  const [formData, setFormData] = useState<CreateMemorialData>({
    name: '',
    memberSince: new Date().getFullYear(),
    passedAway: new Date().getFullYear(),
    yearsActive: undefined,
    bio: '',
    contributions: [],
    photo: ''
  })
  const [contributionInput, setContributionInput] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.name || !formData.bio) {
      return
    }

    try {
      const yearsActive = formData.passedAway - formData.memberSince
      await onCreateMemorial({
        ...formData,
        yearsActive
      })
      
      // Reset form
      setFormData({
        name: '',
        memberSince: new Date().getFullYear(),
        passedAway: new Date().getFullYear(),
        yearsActive: undefined,
        bio: '',
        contributions: [],
        photo: ''
      })
      setContributionInput('')
    } catch (error) {
      // Error handling is done in parent component
    }
  }

  const handleAddContribution = () => {
    if (contributionInput.trim() && !formData.contributions.includes(contributionInput.trim())) {
      setFormData(prev => ({
        ...prev,
        contributions: [...prev.contributions, contributionInput.trim()]
      }))
      setContributionInput('')
    }
  }

  const handleRemoveContribution = (contribution: string) => {
    setFormData(prev => ({
      ...prev,
      contributions: prev.contributions.filter(c => c !== contribution)
    }))
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && e.target === e.currentTarget) {
      e.preventDefault()
      handleAddContribution()
    }
  }

  if (!isOpen) return null

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex min-h-screen items-center justify-center p-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black bg-opacity-50"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="relative w-full max-w-2xl bg-white dark:bg-gray-800 rounded-xl shadow-2xl"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                Add Memorial
              </h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-rb-gold focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Enter member name"
                  required
                />
              </div>

              {/* Years */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Member Since *
                  </label>
                  <input
                    type="number"
                    min="1990"
                    max={new Date().getFullYear()}
                    value={formData.memberSince}
                    onChange={(e) => setFormData(prev => ({ ...prev, memberSince: parseInt(e.target.value) }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-rb-gold focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Passed Away *
                  </label>
                  <input
                    type="number"
                    min="1990"
                    max={new Date().getFullYear()}
                    value={formData.passedAway}
                    onChange={(e) => setFormData(prev => ({ ...prev, passedAway: parseInt(e.target.value) }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-rb-gold focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    required
                  />
                </div>
              </div>

              {/* Photo Upload */}
              <PhotoUpload
                currentPhotoUrl={formData.photo}
                onPhotoUploaded={(photoUrl) => setFormData(prev => ({ ...prev, photo: photoUrl }))}
                onPhotoRemoved={() => setFormData(prev => ({ ...prev, photo: '' }))}
              />

              {/* Bio */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Biography *
                </label>
                <textarea
                  value={formData.bio}
                  onChange={(e) => setFormData(prev => ({ ...prev, bio: e.target.value }))}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-rb-gold focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Share their story and what made them special to our community..."
                  required
                />
              </div>

              {/* Contributions */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Contributions to Community
                </label>
                <div className="flex items-center space-x-2 mb-3">
                  <input
                    type="text"
                    value={contributionInput}
                    onChange={(e) => setContributionInput(e.target.value)}
                    onKeyPress={handleKeyPress}
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-rb-gold focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="Add a contribution..."
                  />
                  <button
                    type="button"
                    onClick={handleAddContribution}
                    className="px-3 py-2 bg-rb-gold text-rb-dark rounded-lg hover:bg-yellow-400 transition-colors"
                  >
                    <PlusIcon className="w-5 h-5" />
                  </button>
                </div>
                
                {formData.contributions.length > 0 && (
                  <div className="space-y-2">
                    {formData.contributions.map((contribution, index) => (
                      <div key={index} className="flex items-center justify-between bg-gray-50 dark:bg-gray-700 px-3 py-2 rounded-lg">
                        <span className="text-sm text-gray-700 dark:text-gray-300">{contribution}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveContribution(contribution)}
                          className="text-red-500 hover:text-red-700 transition-colors"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Form Actions */}
              <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLoading || !formData.name || !formData.bio}
                  className="px-4 py-2 text-sm font-medium text-rb-dark bg-rb-gold border border-transparent rounded-lg hover:bg-yellow-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-rb-gold disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isLoading ? 'Creating...' : 'Create Memorial'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    </AnimatePresence>
  )
}
